'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { getAuthToken, verifyToken } from '@/lib/auth'
import {
  isGuestAuthPath,
  resolveAuthenticatedAwayFromGuestPath,
} from '@/lib/authenticatedGuestPaths'

/**
 * When a valid session exists, keep users off guest auth routes (/signin, /login, enterprise sign-in).
 * Handles client navigations and bfcache restores (pageshow).
 */
export default function AuthenticatedGuestGuard() {
  const pathname = usePathname()
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!isGuestAuthPath(pathname)) return

    const run = async () => {
      if (!getAuthToken()) return
      if (inFlightRef.current) return
      inFlightRef.current = true
      try {
        const { valid } = await verifyToken()
        if (!valid) return
        const target = await resolveAuthenticatedAwayFromGuestPath(pathname)
        window.location.replace(target)
      } finally {
        inFlightRef.current = false
      }
    }

    void run()
  }, [pathname])

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      const path = window.location.pathname
      if (!isGuestAuthPath(path)) return
      if (!getAuthToken()) return
      void (async () => {
        const { valid } = await verifyToken()
        if (!valid) return
        const target = await resolveAuthenticatedAwayFromGuestPath(path)
        window.location.replace(target)
      })()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  return null
}
